import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import AlertsContext from "common/providers/alerts";
import { ObservationGrid } from "types/ObservationGrid";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import usePark from "common/hooks/usePark";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import ObservationGridItem from "./ObservationGridItem";
import restoreItem from "./utils/restoreItem";

function ObservationGrids() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { park } = usePark();

  const { user: currentUser } = useAuth();

  const cantManageObservationGrids =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["observation-grid"]
          ?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<ObservationGrid>("observation-grids", {
    defaultFilters: park?.id
      ? {
          park: park.id,
        }
      : {}, // TODO ajust
    defaultSort: "name:ASC",
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const { updateItem } = useItem<ObservationGrid>("observation-grids", "");

  const handleRestoreItem = useCallback(
    async (item: any) => {
      removeListItem(item?.id);
      await restoreItem(updateItem, {
        ...item,
        park: item?.park?.id ?? park?.id,
      });
      fetchItems();
    },
    [updateItem, fetchItems, removeListItem, park]
  );

  const handleNavigate = (id: any) => {
    navigate(`/observation-grids/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-[2]",
      },
      {
        Header: t("common.isArchived"),
        filterName: "isArchived",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch rightLabel={t("common.isArchived")} {...props} />
          </div>
        ),
        // headerClasses: "mb-3",
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.observationGrids")}
        subtitle={t("titleLegends.observationGrids")}
        btnTitle={t("newItem.observationGrid")}
        btnSubtitle={t("newItem.observationGridLegend")}
        Icon={TableCellsIcon}
        addLink="/observation-grids/add"
        addLinkDisabled={!cantManageObservationGrids}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={ObservationGridItem}
        onDelete={handleRestoreItem}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default ObservationGrids;
